import Editor from '@toast-ui/editor';
import '@toast-ui/editor/dist/toastui-editor.css';
import { Picker } from 'emoji-picker-element';

export const composerMixin = {

    //********************* INIT EDITOR ********************************//
    // Creates a Toast UI WYSIWYG editor in the compose box. Called after every
    // super.render() so the fresh DOM gets a fresh editor instance.
    initEditor() {
        this.tuiEditor = null;
        const container = this.querySelector('.compose_editor_container');
        if(!container) return;

        const isMobile = window.matchMedia('(max-width: 767px)').matches;

        this.tuiEditor = new Editor({
            el: container,
            initialEditType: 'wysiwyg',
            height: 'auto',
            minHeight: isMobile ? '24px' : '80px',
            hideModeSwitch: true,
            toolbarItems: [
                ['bold', 'link', 'ol', 'code', 'codeblock'],
                ['italic', 'strike', 'quote', 'ul'],
            ],
            usageStatistics: false,
            autofocus: false,
            hooks: {
                addImageBlobHook: (blob) => {
                    const img = new Image();
                    img.onload = () => {
                        const canvas = document.createElement('canvas');
                        canvas.width = img.naturalWidth;
                        canvas.height = img.naturalHeight;
                        canvas.getContext('2d').drawImage(img, 0, 0);
                        canvas.toBlob((jpegBlob) => {
                            const now = new Date();
                            const month = now.toLocaleString('en-US', { month: 'short' }).toLowerCase();
                            const day = now.getDate();
                            const year = now.getFullYear();
                            const hours = now.getHours();
                            const minutes = String(now.getMinutes()).padStart(2, '0');
                            const file = new File([jpegBlob], `screenshot-${month}${day}-${year}-${hours}-${minutes}.jpg`, { type: 'image/jpeg' });
                            this.addPendingAttachment(file);
                        }, 'image/jpeg', 0.8);
                        URL.revokeObjectURL(img.src);
                    };
                    img.src = URL.createObjectURL(blob);
                }
            }
        });

        // Enter = send (desktop only - on mobile Enter just makes a new line,
        // sending is via the dedicated Send button), any modified Enter
        // (Shift/Alt/Ctrl/Cmd) = newline (mention dropdown intercepts Enter
        // when open)
        this.tuiEditor.addHook('keydown', (type, e) => {
            if(this.mentionDropdownOpen && this.handleMentionKeydown(e)) return;
            if(e.key !== 'Enter') return;

            if(!isMobile && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
                e.stopPropagation();
                this.sendMessage();
                return;
            }

            // Shift+Enter already inserts a newline via the editor's own
            // keymap (bound to the same splitBlock command as plain Enter).
            // Alt/Ctrl/Cmd+Enter have no such binding - ProseMirror just
            // swallows them and does nothing - so insert the newline
            // ourselves the same way, for a consistent shortcut everywhere.
            if(e.altKey || e.ctrlKey || e.metaKey) {
                e.preventDefault();
                e.stopPropagation();
                this.tuiEditor.insertText('\n');
            }
        });

        // Mobile: Enter just inserts a newline (sending is via the dedicated
        // Send button, matching Slack's mobile UX) - the native default
        // already does this, so there's nothing to intervene on. The one
        // exception is the mention dropdown: mobile virtual keyboards'
        // Enter/Go key often doesn't dispatch a real 'keydown' with key
        // 'Enter' on contenteditable (many IMEs go straight to a DOM
        // mutation), so the keydown hook's handleMentionKeydown() above
        // never fires there. 'beforeinput' with inputType 'insertParagraph'
        // is the reliable cross-platform signal for an Enter press, used
        // here to select the highlighted mention instead of inserting a break.
        const editableEl = this.querySelector('.toastui-editor-ww-container [contenteditable]');
        if(editableEl) {
            editableEl.addEventListener('beforeinput', (e) => {
                if(e.inputType !== 'insertParagraph') return;
                if(!this.mentionDropdownOpen || !this.mentionFilteredUsers.length) return;

                e.preventDefault();
                const user = this.mentionFilteredUsers[this.mentionSelectedIndex];
                if(user) this.selectMention(String(user.user_id));
            });
        }

        // Wire mention detection via editor's change event (fires after ProseMirror commits DOM updates)
        this.tuiEditor.on('change', () => {
            requestAnimationFrame(() => {
                this.detectMentionTrigger();
                this.updateMentionHighlights();
            });
        });

        // Wire emoji button
        const emojiBtn = this.querySelector('.compose_emoji_btn');
        if(emojiBtn) emojiBtn.addEventListener('click', e => this.openComposeEmojiPicker(e));

        // Wire @ mention button
        const mentionBtn = this.querySelector('.compose_mention_btn');
        if(mentionBtn) mentionBtn.addEventListener('click', () => this.insertAtSymbolAndOpenPicker());

        // Wire Aa button (mobile-only formatting toolbar toggle)
        const aaBtn = this.querySelector('.compose_aa_btn');
        if(aaBtn) aaBtn.addEventListener('click', () => this.toggleComposeToolbar());

        // Close mention dropdown on click outside
        this._mentionClickOutside = (e) => {
            if(this.mentionDropdownOpen && this.mentionDropdownEl && !this.mentionDropdownEl.contains(e.target)) {
                this.closeMentionDropdown();
            }
        };
        document.addEventListener('click', this._mentionClickOutside);
    },

    //********************* TOGGLE COMPOSE TOOLBAR ********************************//
    // Mobile-only: shows/hides the formatting toolbar, toggled by the "Aa" button.
    toggleComposeToolbar() {
        const box = this.querySelector('.compose_box');
        if(box) box.classList.toggle('compose_box--toolbar-open');
    },

    //********************* OPEN COMPOSE EMOJI PICKER ********************************//
    // Opens the emoji-picker-element popover above the compose actions bar.
    // Lazy-creates the picker on first use; subsequent clicks toggle visibility.
    // * event (MouseEvent) — click on .compose_emoji_btn
    openComposeEmojiPicker(event) {
        event.stopPropagation();
        const actionsBar = this.querySelector('.compose_actions');
        let popover = actionsBar?.querySelector('.compose-emoji-picker-popover');

        if(!popover) {
            const picker = new Picker({ locale: 'en' });
            popover = document.createElement('div');
            popover.className = 'compose-emoji-picker-popover emoji-picker-popover';
            popover.append(picker);
            actionsBar.append(popover);

            picker.addEventListener('emoji-click', e => {
                const unicode = e.detail.unicode;
                if(this.tuiEditor && unicode) {
                    this.tuiEditor.insertText(unicode);
                    this.tuiEditor.focus();
                }
                popover.style.display = 'none';
            });

            document.addEventListener('click', e => {
                if(popover.style.display === 'block' && !popover.contains(e.target) && e.target !== event.currentTarget) {
                    popover.style.display = 'none';
                }
            });
        }

        if(popover.style.display === 'block') {
            popover.style.display = 'none';
            return;
        }

        popover.style.display = 'block';
        this.positionComposeEmojiPicker(event.currentTarget, popover);
    },

    //********************* POSITION COMPOSE EMOJI PICKER ********************************//
    // Positions the compose emoji popover above the actions bar button that opened it.
    // * btn (HTMLElement) — the emoji button
    // * popover (HTMLElement) — the popover element
    positionComposeEmojiPicker(btn, popover) {
        const btnRect = btn.getBoundingClientRect();
        popover.style.position = 'fixed';
        popover.style.bottom = `${window.innerHeight - btnRect.top + 4}px`;
        popover.style.left = `${btnRect.left}px`;
        popover.style.zIndex = '1000';
    },

};
